import { useEffect, useState, useRef } from "react";
import "../styles/leaderships.css";

import heroBg          from "../photos/leaderships.jpg";

// Shared photos (same imports as projects.js)
import capstonePhoto   from "../photos/projects23.jpeg";
import nbcPhoto        from "../photos/projects06.png";
import circuitKitPhoto from "../photos/projects08.png";
import bjtCurvesPhoto  from "../photos/projects09.png";
import bjtSwitchPhoto  from "../photos/projects10.png";
import photoboothPhoto from "../photos/projects12.png";
import pcbPhoto        from "../photos/projects13.png";
import streetPhoto     from "../photos/projects14.png";
import bcdPhoto        from "../photos/projects16.png";
import windPhoto       from "../photos/projects17.png";
import flashAdcPhoto   from "../photos/projects18.png";
import bwDacPhoto      from "../photos/projects19.png";
import r2rDacPhoto     from "../photos/projects20.png";
import packetPhoto     from "../photos/projects21.png";
import seqPhoto        from "../photos/projects22.png";
import sumobotPhoto    from "../photos/project0.jpeg";
import vendingPhoto    from "../photos/projects04.png";
import bombRobotPhoto  from "../photos/projects05.jpeg";
import firestorePhoto  from "../photos/project3.png";
import roadSignPhoto   from "../photos/projects07.png";
import riscvPhoto      from "../photos/projects01.png";
import picoPark        from "../photos/projects02.png";
import parisPhoto      from "../photos/projects24.png";
import graduatePhoto   from "../photos/projects22.png";

/* ═══════════════════════════════════════
   ORG DATA
═══════════════════════════════════════ */

const ORG_ROLE = {
  id: "org-00",
  org: "ICpEP.SE-FC",
  fullName: "Institute of Computer Engineers of the Philippines — Student Edition, FAITH Colleges Chapter",
  role: "Officer",
  period: "SY 2025 – 2026",
  color: "cyan",
  stat: "SY 25–26",
  statLabel: "Officer",
  detail:
    "Serving as an officer of ICpEP.SE-FC — the premier professional student organization for computer engineering at FAITH Colleges. As an officer, I help drive technical events, inter-college engagements, and community initiatives that connect students to the broader engineering profession. This role demands both organizational discipline and the ability to inspire peers toward a shared vision.",
  tags: ["Organization", "Event Management", "Technical Leadership", "Peer Mentoring", "CpE Community"],
};

/* ═══════════════════════════════════════
   LEADERSHIP ITEMS
═══════════════════════════════════════ */

const LEADERSHIP_ITEMS = [

  /* ── SHS ── */
  {
    id: "lead-shs-01",
    yearKey: "shs",
    year: "SHS · 2021–2023",
    category: "Capstone Project",
    title: "Decipher: A Floor Wax Applicator",
    role: "Lead Researcher & Lead Author",
    color: "gold",
    stat: "SHS",
    statLabel: "Capstone",
    photo: capstonePhoto,
    detail:
      "Led the research and documentation of our senior high school capstone — the moment theory became something tangible. As lead researcher and author, I directed the engineering methodology, coordinated the team's prototype development, and produced the final research paper that represented our collective STEM journey.",
    tags: ["Engineering Research", "Prototyping", "Mechanical", "Teamwork", "Technical Writing"],
  },

  /* ── 1st Year ── */
  {
    id: "lead-y1-01",
    yearKey: "y1",
    year: "1st Year · 2023–2024",
    category: "Software",
    title: "Number-Base Conversion System",
    role: "Lead Developer",
    color: "cyan",
    stat: "4",
    statLabel: "Base Systems",
    photo: nbcPhoto,
    detail:
      "Led the development of an interactive number-base conversion system covering binary, octal, decimal, and hexadecimal — one of my earliest projects where I took full ownership of the codebase and documentation. A simple project that opened a vast door into digital logic.",
    tags: ["Digital Logic", "Number Systems", "Programming", "Software Development"],
  },

  /* ── 2nd Year ── */
  {
    id: "lead-y2-01",
    yearKey: "y2",
    year: "2nd Year · 2024–2025",
    category: "Machine Learning",
    title: "VisionPath — Road Sign Classifier",
    role: "ML Engineer & Lead Author",
    color: "purple",
    stat: "CV",
    statLabel: "ML Lead",
    photo: roadSignPhoto,
    detail:
      "Led the machine learning engineering effort — from dataset curation and augmentation through model training and live webcam inference. As lead author, I synthesized the full research narrative, connecting the technical methodology to its real-world impact for visually impaired users.",
    tags: ["Python", "TensorFlow", "OpenCV", "Computer Vision", "Research"],
  },
  {
    id: "lead-y2-02",
    yearKey: "y2",
    year: "2nd Year · 2024–2025",
    category: "Hardware",
    title: "Series & Parallel Circuits Interactive Kit",
    role: "Hardware Designer, Lead Author & Manual Creator",
    color: "teal",
    stat: "EDU",
    statLabel: "Kit Lead",
    photo: circuitKitPhoto,
    detail:
      "Designed the modular educational electronics kit and authored both the technical documentation and the full student instruction manual — ensuring the hardware was not just functional but genuinely teachable for its intended audience.",
    tags: ["Circuit Design", "Electronics", "Prototyping", "Technical Writing"],
  },
  {
    id: "lead-y2-03",
    yearKey: "y2",
    year: "2nd Year · 2024–2025",
    category: "Electronics",
    title: "Voltage Regulator",
    role: "Circuit Designer, Lead Author & Documenter",
    color: "gold",
    stat: "LM",
    statLabel: "7805 Lead",
    photo: null,
    detail:
      "Led the design and full documentation of a linear voltage regulator circuit — validating output stability, ripple reduction, and load regulation against LM7805 datasheet curves. As lead author, I produced the complete laboratory report translating oscilloscope readings into coherent engineering findings.",
    tags: ["Analog Electronics", "LM7805", "Oscilloscope", "Circuit Design", "Technical Writing"],
  },
  {
    id: "lead-y2-04",
    yearKey: "y2",
    year: "2nd Year · 2024–2025",
    category: "Electronics",
    title: "BJT Characteristic Curves",
    role: "Lab Researcher, Lead Author & Data Analyst",
    color: "coral",
    stat: "BJT",
    statLabel: "Lead Analyst",
    photo: bjtCurvesPhoto,
    detail:
      "Led the experimental tracing of BJT characteristic curves, directing lab procedures and coordinating data collection across active, saturation, and cutoff regions. As lead author and data analyst, I compared measured results against SPICE simulation models and produced the final technical report.",
    tags: ["BJT", "Electronics Lab", "Data Analysis", "SPICE", "Technical Writing"],
  },
  {
    id: "lead-y2-05",
    yearKey: "y2",
    year: "2nd Year · 2024–2025",
    category: "Electronics",
    title: "BJT Device as a Switch",
    role: "Lab Researcher, Lead Author & Data Analyst",
    color: "coral",
    stat: "BJT",
    statLabel: "Lead Author",
    photo: bjtSwitchPhoto,
    detail:
      "Led the lab investigation of BJT switching behavior in saturation and cutoff regions, applied to a practical digital control circuit with LED output. As lead author, I documented the full switching analysis and coordinated the team's experimental verification process.",
    tags: ["BJT", "Digital Electronics", "Switching Circuits", "Lab Research", "Documentation"],
  },
  {
    id: "lead-y2-06",
    yearKey: "y2",
    year: "2nd Year · 2024–2025",
    category: "Research",
    title: "Graduate Tracer Study of CpE Graduates",
    role: "Researcher",
    color: "purple",
    stat: "GTS",
    statLabel: "Researcher",
    photo: null,
    detail:
      "Contributed as a researcher in tracking the employment outcomes and academic pathways of Computer Engineering graduates of FAITH Colleges — designing the survey instrument, gathering responses, and presenting findings with clear data visualizations that informed the department's program evaluation.",
    tags: ["Research Methods", "Data Analysis", "Survey Design", "Visualization", "Academic Research"],
  },
  {
    id: "lead-y2-07",
    yearKey: "y2",
    year: "2nd Year · 2024–2025",
    category: "Software",
    title: "Piktyur! Piktyur! — Photobooth App",
    role: "Full-Stack Developer, UI/UX Designer & Lead Author",
    color: "teal",
    stat: "#1",
    statLabel: "Top Rating",
    photo: photoboothPhoto,
    detail:
      "Led the full development lifecycle of the photobooth application — from UI/UX design to OpenCV integration to live event deployment. As lead author I documented every design and engineering decision, and the project earned the highest rating among all OOP class outputs.",
    tags: ["Python", "OpenCV", "GUI", "UI/UX Design", "Full-Stack"],
  },
  {
    id: "lead-y2-08",
    yearKey: "y2",
    year: "2nd Year · 2024–2025",
    category: "PCB Design",
    title: "Two-Digit Decimal Counter PCB",
    role: "Lead PCB Designer & Lead Author",
    color: "gold",
    stat: "DRC",
    statLabel: "Verified",
    photo: pcbPhoto,
    detail:
      "Led the PCB design process from schematic capture through layout and DRC verification — coordinating component placement, trace routing, and design rule compliance before fabrication. Authored the accompanying technical document covering every stage of the design-to-board workflow.",
    tags: ["KiCad", "PCB Design", "7-Segment Display", "DRC", "Electronics"],
  },

  /* ── 3rd Year ── */
  {
    id: "lead-y3-01",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "DSP / Embedded",
    title: "Sift Happens — Wind Speed DSP Device",
    role: "DSP Engineer & Lead Author",
    color: "teal",
    stat: "FIR/IIR",
    statLabel: "Filter Lead",
    photo: windPhoto,
    detail:
      "Led the DSP engineering effort for a real-time digital filtering device, directing filter design, microcontroller integration, and live signal output. Authored the full technical documentation, translating complex frequency-domain theory into accessible engineering writing.",
    tags: ["DSP", "FIR Filter", "IIR Filter", "Microcontroller", "Technical Writing"],
  },
  {
    id: "lead-y3-02",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "Analog / Mixed Signal",
    title: "Flash ADC",
    role: "Circuit Designer & Documentation Specialist",
    color: "gold",
    stat: "ADC",
    statLabel: "Flash Lead",
    photo: flashAdcPhoto,
    detail:
      "Designed a flash (parallel) Analog-to-Digital Converter using a precision resistor ladder and comparator bank, achieving high-speed conversion with minimal latency. As documentation specialist, I produced the complete technical report covering circuit theory, design decisions, and measured results.",
    tags: ["ADC", "Comparators", "Resistor Ladder", "Analog Electronics", "Documentation"],
  },
  {
    id: "lead-y3-03",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "Analog / Mixed Signal",
    title: "Binary Weighted DAC",
    role: "Circuit Designer & Documentation Specialist",
    color: "cyan",
    stat: "DAC",
    statLabel: "BW Lead",
    photo: bwDacPhoto,
    detail:
      "Implemented a binary weighted resistor DAC with op-amp summing amplifier configuration, analyzing linearity, resolution, and full-scale output characteristics. Led the documentation effort that precisely captured the design methodology and measurement outcomes.",
    tags: ["DAC", "Op-Amp", "Binary Weighted Resistor", "Analog Design", "Documentation"],
  },
  {
    id: "lead-y3-04",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "Analog / Mixed Signal",
    title: "R/2R Ladder DAC",
    role: "Circuit Designer & Documentation Specialist",
    color: "cyan",
    stat: "R/2R",
    statLabel: "Ladder Lead",
    photo: r2rDacPhoto,
    detail:
      "Built and tested an R/2R ladder DAC topology, leading both the circuit design and the comparative analysis against binary-weighted designs. Authored all documentation translating precision measurement data and design trade-offs into a clear engineering narrative.",
    tags: ["DAC", "R/2R Ladder", "Op-Amp", "Analog Electronics", "Documentation"],
  },
  {
    id: "lead-y3-05",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "Digital Systems",
    title: "BCD-2421 Code Converter",
    role: "Software Designer, Hardware Designer & Lead Author",
    color: "purple",
    stat: "Full",
    statLabel: "Design Lead",
    photo: bcdPhoto,
    detail:
      "Owned both the software and hardware design of a BCD to 2421 code converter — from truth table derivation and logic minimization through to physical gate-level implementation. Led the documentation effort that captured every design decision with precision.",
    tags: ["Digital Logic", "Logic Gates", "Hardware Verification", "Code Conversion"],
  },
  {
    id: "lead-y3-06",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "Networking",
    title: "Cisco Packet Tracer Mini Projects",
    role: "Network Designer, Lead Author & Documentation Specialist",
    color: "cyan",
    stat: "5+",
    statLabel: "Topologies",
    photo: packetPhoto,
    detail:
      "Designed and documented multiple network topologies across LAN, VLAN, and routing configurations — leading the documentation effort that turned simulation outputs into structured, readable technical reports for each network scenario.",
    tags: ["Cisco", "Packet Tracer", "VLAN", "Routing", "Documentation"],
  },
  {
    id: "lead-y3-07",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "Web / Cloud",
    title: "Firestore Inventory System",
    role: "Full-Stack Developer",
    color: "teal",
    stat: "CRUD",
    statLabel: "Full-Stack",
    photo: firestorePhoto,
    detail:
      "Developed a cloud-connected inventory management web application with real-time Firestore database sync, full CRUD operations, role-based user authentication, and a clean dashboard UI — my deepest and most complete web project to date, built end-to-end.",
    tags: ["Firebase", "Firestore", "React", "JavaScript", "Authentication"],
  },
  {
    id: "lead-y3-08",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "Digital Systems",
    title: "Mealy Machine-Based Vending Machine Controller",
    role: "Lead Author & Hardware Implementer",
    color: "purple",
    stat: "FSM",
    statLabel: "HW Lead",
    photo: vendingPhoto,
    detail:
      "Designed and simulated a Mealy finite state machine for a vending machine controller, mapping every state transition, then physically implemented the logic on hardware using combinational ICs. As lead author, I produced the complete documentation connecting theoretical FSM design to hardware verification.",
    tags: ["FSM", "Mealy Machine", "Digital Logic", "Hardware", "State Diagrams"],
  },
  {
    id: "lead-y3-09",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "Robotics",
    title: "SumoBot",
    role: "Lead Designer & Lead Author",
    color: "coral",
    stat: "1st",
    statLabel: "Place Design",
    photo: sumobotPhoto,
    detail:
      "Directed the mechanical design and programming of an autonomous sumo robot, coordinating sensor integration and motor control logic. As lead author, I documented the full engineering process from concept to competition — a project that became one of the most memorable builds of my college life.",
    tags: ["Arduino", "Robotics", "Autonomous Systems", "C++", "Hardware Design"],
  },
  {
    id: "lead-y3-10",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "Digital Systems",
    title: "Sequential & Combinational Circuit Implementation",
    role: "Verilog Developer, Hardware Implementer & Lead Author",
    color: "teal",
    stat: "Dual",
    statLabel: "Mode Lead",
    photo: seqPhoto,
    detail:
      "Led both the software simulation and physical hardware implementation of sequential and combinational digital circuits — bridging theoretical logic design with real-world verification. Authored all documentation, coordinating between simulation outputs and breadboard results to produce a unified technical record.",
    tags: ["Verilog", "Digital Logic", "Hardware", "Simulation", "Documentation"],
  },
  {
    id: "lead-y3-11",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "IoT / Embedded",
    title: "Smart Streetlight Controller",
    role: "Lead Embedded Developer & Lead Author",
    color: "gold",
    stat: "IoT",
    statLabel: "Lead Dev",
    photo: streetPhoto,
    detail:
      "Spearheaded the embedded development of an IoT-enabled streetlight system with autonomous brightness control, directing sensor integration and assertion-based verification. Authored the full technical report, bridging hardware behavior and formal verification methodology for the team.",
    tags: ["Arduino", "IoT", "PWM", "Assertion-Based Verification", "Embedded C"],
  },
  {
    id: "lead-y3-12",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "Game Dev",
    title: "Pico-Park Parody Game",
    role: "Game Developer & Lead Author",
    color: "gold",
    stat: "2D",
    statLabel: "Game Lead",
    photo: picoPark,
    detail:
      "Led the game development effort — building cooperative multiplayer mechanics from scratch, designing original levels, and coordinating the full technical pipeline for our class game jam submission. As lead author, I produced the complete documentation covering game architecture and design decisions.",
    tags: ["Game Engine", "2D Graphics", "Multiplayer Logic", "Level Design", "Technical Writing"],
  },
  {
    id: "lead-y3-13",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "Robotics",
    title: "Bomb Disposal Robot",
    role: "Lead Author, Designer & Block Diagram Creator",
    color: "coral",
    stat: "RF",
    statLabel: "Lead Design",
    photo: bombRobotPhoto,
    detail:
      "Directed the design documentation of a remotely controlled bomb disposal robot — producing all block diagrams, system architecture drawings, and the complete technical report. As designer and lead author, I translated every hardware decision into a clear and structured engineering narrative.",
    tags: ["Arduino", "RF Control", "Servo Motors", "Chassis Design", "Block Diagram"],
  },
  {
    id: "lead-y3-14",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "Computer Architecture",
    title: "RISC-V Processor Design",
    role: "System Verilog Developer & Lead Author",
    color: "gold",
    stat: "RV32I",
    statLabel: "ISA Subset",
    photo: riscvPhoto,
    detail:
      "Led the design and documentation of a simplified RISC-V processor pipeline in SystemVerilog — coordinating fetch, decode, execute, memory, and write-back stages. As lead author, I was responsible for translating every architectural decision into clear technical writing.",
    tags: ["RISC-V", "SystemVerilog", "Pipelining", "HDL", "Technical Writing"],
  },
  {
    id: "lead-y3-15",
    yearKey: "y3",
    year: "3rd Year · 2025–2026",
    category: "Multimedia & Storytelling",
    title: "A Taste of Paris",
    role: "Lead Designer, Lead Developer, Lead Author & Storyteller",
    color: "purple",
    stat: "Full",
    statLabel: "Stack Lead",
    photo: parisPhoto,
    detail:
      "Took full creative and technical ownership of this multimedia project — conceptualizing the narrative arc, designing the visual system, building the interactive experience, and writing all accompanying documentation. Leading across design, development, and authorship simultaneously was a test of versatility and creative vision.",
    tags: ["UI Design", "Multimedia", "Storytelling", "Full-Stack", "Creative Direction"],
  },
];

/* ═══════════════════════════════════════
   TICKER — scrolling role tape
═══════════════════════════════════════ */
const TICKER_ITEMS = [
  "Lead Author", "ICpEP Officer", "ML Engineer", "Circuit Designer",
  "Full-Stack Developer", "PCB Designer", "DSP Engineer", "Game Developer",
  "Network Designer", "Hardware Implementer", "Robotics Lead", "UI/UX Designer",
  "Verilog Developer", "Embedded Systems", "Lead Researcher", "Documentation Specialist",
];

function Ticker() {
  // Duplicate for seamless loop
  const items = [...TICKER_ITEMS, ...TICKER_ITEMS];
  return (
    <div className="ldr-ticker" aria-hidden="true">
      <div className="ldr-ticker__track">
        {items.map((item, i) => (
          <span key={i} className="ldr-ticker__item">
            {item}
            <span className="ldr-ticker__sep">·</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   ORG SECTION — editorial block, no flip
═══════════════════════════════════════ */
function OrgSection({ item }) {
  return (
    <div className="ldr-org ldr-reveal">
      {/* Top rule with label */}
      <div className="ldr-org__rule">
        <span className="ldr-org__rule-label">Organizational Leadership</span>
      </div>

      <div className="ldr-org__body">
        {/* Left — identity */}
        <div className="ldr-org__left">
          <p className="ldr-org__period">{item.period}</p>
          <h2 className="ldr-org__name">{item.org}</h2>
          <p className="ldr-org__fullname">{item.fullName}</p>
          <span className="ldr-org__role-pill">{item.role}</span>
        </div>

        {/* Vertical rule */}
        <div className="ldr-org__vline" />

        {/* Right — narrative */}
        <div className="ldr-org__right">
          <p className="ldr-org__pull">
            "Leadership isn't a title —<br />it's showing up when the work needs a name on it."
          </p>
          <p className="ldr-org__detail">{item.detail}</p>
          <div className="ldr-org__tags">
            {item.tags.map((t) => (
              <span key={t} className="ldr-tag is-visible">{t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom rule */}
      <div className="ldr-org__bottom-rule" />
    </div>
  );
}

/* ═══════════════════════════════════════
   HELPERS
═══════════════════════════════════════ */

function useReveal() {
  useEffect(() => {
    const run = () => {
      const nodes = document.querySelectorAll(".ldr-reveal");
      const obs = new IntersectionObserver(
        (entries) =>
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("is-visible");
              obs.unobserve(e.target);
            }
          }),
        { threshold: 0.07, rootMargin: "0px 0px -24px 0px" }
      );
      nodes.forEach((n) => obs.observe(n));
      return obs;
    };
    const obs = run();
    return () => obs.disconnect();
  }, []);
}

function useFilterReveal(filter) {
  useEffect(() => {
    const t = setTimeout(() => {
      const nodes = document.querySelectorAll(".ldr-reveal:not(.is-visible)");
      nodes.forEach((n) => { void n.offsetHeight; n.classList.add("is-visible"); });
    }, 60);
    return () => clearTimeout(t);
  }, [filter]);
}

/* ═══════════════════════════════════════
   LEADER CARD
═══════════════════════════════════════ */
function LeaderCard({ item, index }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      className={`ldr-card ldr-reveal ldr-card--${item.color}`}
      style={{ transitionDelay: `${(index % 3) * 0.07}s` }}
      onClick={() => setFlipped((f) => !f)}
      title="Click to read more"
    >
      <div className={`ldr-card__inner ${flipped ? "is-flipped" : ""}`}>
        <div className="ldr-card__face ldr-card__front">
          <div className="ldr-card__stat-wrap">
            <span className="ldr-card__stat">{item.stat}</span>
            <span className="ldr-card__stat-label">{item.statLabel}</span>
          </div>
          <div className="ldr-card__photo-wrap">
            {item.photo ? (
              <>
                <img src={item.photo} alt={item.title} className="ldr-card__photo" />
                <div className="ldr-card__photo-overlay" />
              </>
            ) : (
              <div className="ldr-card__photo-overlay" style={{
                position: "absolute", inset: 0,
                background: "repeating-linear-gradient(45deg,#0e0e0e,#0e0e0e 8px,#0c0c0c 8px,#0c0c0c 16px)",
                display: "flex", alignItems: "center", justifyContent: "center"
              }}>
                <span style={{ fontSize: 28, opacity: 0.1 }}>⬡</span>
              </div>
            )}
          </div>
          <div className="ldr-card__body">
            <p className="ldr-card__year">{item.year}</p>
            <p className="ldr-card__category">{item.category}</p>
            <p className="ldr-card__title">{item.title}</p>
            <p className="ldr-card__role">{item.role}</p>
          </div>
          <span className="ldr-card__flip-hint">flip ↻</span>
        </div>
        <div className="ldr-card__face ldr-card__back">
          <p className="ldr-card__back-category">{item.category}</p>
          <p className="ldr-card__back-detail">{item.detail}</p>
          <div className="ldr-card__tags">
            {item.tags.map((t) => (
              <span key={t} className="ldr-tag is-visible">{t}</span>
            ))}
          </div>
          <span className="ldr-card__flip-hint">flip ↻</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   FILTER BAR
═══════════════════════════════════════ */
const FILTERS = [
  { key: "all", label: "All" },
  { key: "y3",  label: "3rd Year" },
  { key: "y2",  label: "2nd Year" },
  { key: "y1",  label: "1st Year" },
  { key: "shs", label: "SHS" },
];

/* ═══════════════════════════════════════
   MAIN EXPORT
═══════════════════════════════════════ */
export default function Leaderships() {
  const [filter, setFilter] = useState("all");
  useReveal();
  useFilterReveal(filter);

  const filtered =
    filter === "all"
      ? LEADERSHIP_ITEMS
      : LEADERSHIP_ITEMS.filter((i) => i.yearKey === filter);

  return (
    <div className="ldr-page">

      {/* ═══ HERO ═══ */}
      <div className="ldr-hero">
        <img src={heroBg} alt="" className="ldr-hero__bg" aria-hidden="true" />
        <div className="ldr-hero__overlay" />
        <div className="ldr-hero__content">
          <p className="ldr-hero__eyebrow">— Roles, Responsibilities & Impact —</p>
          <h1 className="ldr-hero__title">
            <span className="ldr-hero__title-line">Leading</span>
            <span className="ldr-hero__title-line ldr-hero__title-line--accent">From the Front</span>
          </h1>
          <p className="ldr-hero__sub">
            Every lead role is a decision to be accountable — not just for the output,
            but for the people and ideas behind it.
          </p>
        </div>
        <div className="ldr-hero__badges" aria-hidden="true">
          <span className="ldr-badge ldr-badge--1">Lead Author</span>
          <span className="ldr-badge ldr-badge--2">ICpEP Officer</span>
          <span className="ldr-badge ldr-badge--3">Lead Developer</span>
          <span className="ldr-badge ldr-badge--4">Lead Designer</span>
          <span className="ldr-badge ldr-badge--5">ML Engineer</span>
        </div>
      </div>

      {/* ═══ TICKER ═══ */}
      <Ticker />

      <div className="ldr-divider ldr-reveal" />

      {/* ═══ ORG SECTION ═══ */}
      <OrgSection item={ORG_ROLE} />

      <div className="ldr-divider ldr-reveal" />

      {/* ═══ PROJECT LEADERSHIP ═══ */}
      <div className="ldr-section-label ldr-reveal">— Project Leadership —</div>

      <div className="ldr-filter-bar ldr-reveal">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`ldr-filter-btn ${filter === f.key ? "is-active" : ""}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            <span className="ldr-filter-btn__count">
              {f.key === "all"
                ? LEADERSHIP_ITEMS.length
                : LEADERSHIP_ITEMS.filter((i) => i.yearKey === f.key).length}
            </span>
          </button>
        ))}
      </div>

      <div className="ldr-grid">
        {filtered.map((item, i) => (
          <LeaderCard key={`${filter}-${item.id}`} item={item} index={i} />
        ))}
      </div>

      <p className="ldr-footnote ldr-reveal">
        — Each card represents a project where I stepped forward as the primary decision-maker,
        author, designer, or engineer. Flip to read the full leadership story. —
      </p>

    </div>
  );
}